/**
 * Connect the SAP VIM driver to a real tenant.
 *
 * The driver has always been a real client — four transports, OAuth2, CSRF,
 * watermarked delta. What it lacked was anywhere to put the tenant's details,
 * so it stayed on its built-in simulator and the console said LIVE the moment
 * somebody flipped a flag. This panel is the missing half:
 *
 *   · only the fields the chosen transport actually needs are shown, each with
 *     what it is for,
 *   · the server refuses LIVE until they are all present and names what is
 *     missing,
 *   · "Go live" performs a real read before it claims to be connected.
 */
import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2, Loader2, Plug, Save, Zap } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useIptTheme } from '@/components/ipt/iptTheme';
import { Btn, Field, Input, Panel, Select } from '@/components/ipt/IptPrimitives';

const TRANSPORTS = [
  ['odata', 'SAP Gateway (OData)', 'Direct HTTPS to the gateway host'],
  ['cloud_connector', 'On-premise via SAP Cloud Connector', 'Routed with a location id'],
  ['btp_destination', 'SAP BTP Destination service', 'Address and auth resolved from the destination'],
  ['sftp', 'CSV / XML drop over SFTP', 'For tenants that will not open an API'],
];

const AUTH = [['oauth2', 'OAuth2 client credentials'], ['basic', 'Basic (SAP user)'],
  ['none', 'None — handled by the transport']];

// Every field the panel can show, with what it is and whether it is a secret.
const FIELDS = {
  base_url: ['Base URL', 'https://sapgw.yourco.com — scheme and host only'],
  odata_service: ['OData service', 'e.g. ZVIM_DP_SRV'],
  odata_version: ['OData version', 'v2 returns d.results · v4 returns value'],
  sap_client: ['sap-client', 'Only when the host needs the client number'],
  location_id: ['Cloud Connector location id', 'As configured in the Cloud Connector'],
  token_url: ['Token URL', 'OAuth2 token endpoint'],
  client_id: ['Client id', 'From the OAuth client'],
  client_secret: ['Client secret', 'Stored encrypted; shown masked', true],
  oauth_scope: ['Scope', 'Optional, when the tenant requires one'],
  username: ['SAP user', 'Service user with read access to the VIM services'],
  password: ['SAP password', 'Stored encrypted; shown masked', true],
  destination_name: ['Destination name', 'As named in the BTP subaccount'],
  dest_service_url: ['Destination service URL', 'From the destination service key (uri)'],
  dest_token_url: ['XSUAA token URL', 'service key url + /oauth/token'],
  dest_client_id: ['Destination client id', 'clientid from the service key'],
  dest_client_secret: ['Destination client secret', 'clientsecret from the service key', true],
  ca_bundle_path: ['CA bundle path', 'PEM on disk, for an internal certificate authority'],
  client_cert_path: ['Client certificate path', 'For mutual TLS'],
  client_key_path: ['Client key path', 'For mutual TLS'],
  sftp_password: ['SFTP password', 'Or supply a private key instead', true],
  private_key: ['SFTP private key', 'PEM, RSA / Ed25519 / ECDSA', true],
  webhook_secret: ['Event webhook secret', 'Shared secret for SAP Event Mesh pushes', true],
};

const SFTP_FIELDS = [['host', 'Host'], ['port', 'Port'], ['username', 'Username'],
  ['remote_dir', 'Remote directory'], ['file_pattern', 'File pattern'],
  ['archive_dir', 'Archive directory']];

const SHOWN = {
  odata: ['base_url', 'odata_service', 'odata_version', 'sap_client'],
  cloud_connector: ['base_url', 'odata_service', 'odata_version', 'location_id', 'sap_client'],
  btp_destination: ['destination_name', 'dest_service_url', 'dest_token_url',
    'dest_client_id', 'dest_client_secret', 'odata_service', 'odata_version'],
  sftp: [],
};
const AUTH_SHOWN = {
  oauth2: ['token_url', 'client_id', 'client_secret', 'oauth_scope'],
  basic: ['username', 'password'],
  none: [],
};

export const SapVimConnect = ({ name = 'sap_vim', health, onChanged }) => {
  const T = useIptTheme();
  const [req, setReq] = useState(null);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(null);
  const [probe, setProbe] = useState(null);

  const load = useCallback(async () => {
    const [r, c] = await Promise.all([
      api.get(`/connectors/${name}/requirements`).then((x) => x.data).catch(() => null),
      api.get(`/connectors/${name}/config`).then((x) => x.data).catch(() => null),
    ]);
    setReq(r);
    setDraft(c || {});
  }, [name]);

  useEffect(() => { load(); }, [load]);

  if (!draft) {
    return (
      <Panel T={T} testId="sapvim-connect" title="Connect to SAP">
        <Loader2 className="w-4 h-4 animate-spin mx-auto" style={{ color: T.primary }} />
      </Panel>
    );
  }

  const transport = draft.transport || 'odata';
  const authMode = draft.auth_mode || 'oauth2';
  const live = (draft.mode || 'SIMULATED') === 'LIVE';
  const missing = health?.missingForLive || req?.missing || [];
  const connected = Boolean(health?.connected);

  const set = (k, v) => setDraft((p) => ({ ...p, [k]: v }));
  const setSftp = (k, v) => setDraft((p) => ({ ...p, sftp: { ...(p.sftp || {}), [k]: v } }));

  const payload = () => {
    const out = {};
    ['transport', 'auth_mode', 'base_url', 'odata_service', 'odata_version', 'sap_client',
      'location_id', 'token_url', 'client_id', 'client_secret', 'oauth_scope', 'username',
      'password', 'destination_name', 'dest_service_url', 'dest_token_url', 'dest_client_id',
      'dest_client_secret', 'ca_bundle_path', 'client_cert_path', 'client_key_path',
      'sftp_password', 'private_key', 'webhook_secret', 'verify_tls', 'timeout_sec',
      'write_back_enabled'].forEach((k) => {
      if (draft[k] !== undefined && draft[k] !== null) out[k] = draft[k];
    });
    if (draft.sftp) out.sftp = draft.sftp;
    return out;
  };

  const save = async (extra = {}) => {
    setBusy('save');
    try {
      await api.put(`/connectors/${name}/config`, { ...payload(), ...extra });
      toast.success(extra.mode === 'LIVE' ? 'Switched to LIVE — testing the connection now'
        : 'Saved');
      if (extra.mode === 'LIVE') await test();
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save', { duration: 9000 });
    } finally { setBusy(null); }
  };

  const test = async () => {
    setBusy('test');
    try {
      const r = await api.post(`/connectors/${name}/test`);
      setProbe(r.data);
      (r.data?.connected ? toast.success : toast.error)(
        r.data?.connected ? 'SAP answered' : 'SAP did not answer',
        { description: r.data?.detail || r.data?.error, duration: 9000 });
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'The test could not run');
    } finally { setBusy(null); }
  };

  const keys = [...(SHOWN[transport] || []),
    ...(transport === 'btp_destination' ? [] : (AUTH_SHOWN[authMode] || [])),
    'ca_bundle_path', 'client_cert_path', 'client_key_path', 'webhook_secret'];

  return (
    <Panel T={T} testId="sapvim-connect" title="Connect to SAP"
      subtitle="Fill the fields for your transport, save, then test. The connector only
                reports LIVE once a real read has succeeded."
      right={(
        <div className="flex items-center gap-2">
          <Btn T={T} variant="ghost" onClick={test} disabled={busy === 'test'}
            testId="sapvim-test">
            {busy === 'test' ? <Loader2 className="w-3 h-3 animate-spin" />
              : <Zap className="w-3 h-3" />} Test connection
          </Btn>
          <Btn T={T} onClick={() => save()} disabled={busy === 'save'} testId="sapvim-save">
            <Save className="w-3 h-3" /> Save
          </Btn>
          {live ? (
            <Btn T={T} variant="warn" testId="sapvim-simulate"
              onClick={() => save({ mode: 'SIMULATED' })}>Back to simulator</Btn>
          ) : (
            <Btn T={T} variant="ok" testId="sapvim-golive"
              onClick={() => save({ mode: 'LIVE' })}>Go live</Btn>
          )}
        </div>
      )}>
      <div className="px-3 py-2 rounded-md text-[11.5px] flex items-start gap-2 mb-3"
        data-testid="sapvim-state"
        style={{ background: T.tint(connected ? T.green : T.accent, '14'),
          color: connected ? T.green : T.accent,
          border: `1px solid ${T.tint(connected ? T.green : T.accent, '3A')}` }}>
        {connected ? <CheckCircle2 className="w-4 h-4 mt-px shrink-0" />
          : <Plug className="w-4 h-4 mt-px shrink-0" />}
        <span>
          <b>{health?.labelled || (live ? 'LIVE' : 'SIMULATED')}</b>
          {(probe?.detail || health?.probe?.detail)
            && <> — {probe?.detail || health?.probe?.detail}</>}
          {probe?.url && <div className="font-mono text-[10px] mt-0.5">{probe.url}</div>}
          {probe?.status !== undefined && (
            <div className="text-[10px] mt-0.5">
              HTTP {probe.status} · {probe.rowsSeen} row(s) read · {probe.elapsedMs} ms
            </div>
          )}
        </span>
      </div>

      {!live && missing.length > 0 && (
        <div className="px-3 py-2 rounded-md text-[11px] mb-3" data-testid="sapvim-missing"
          style={{ background: T.tint(T.amber, '12'), border: `1px solid ${T.tint(T.amber, '33')}` }}>
          <div className="flex items-center gap-1.5 font-semibold mb-1" style={{ color: T.amber }}>
            <AlertTriangle className="w-3.5 h-3.5" /> Still needed before this can go live
          </div>
          <ul className="space-y-0.5" style={{ color: T.body }}>
            {missing.map((m) => (
              <li key={m.field} data-testid={`sapvim-missing-${m.field}`}>
                <span className="font-mono">{m.field}</span> — {m.needs}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
        <Field T={T} label="transport" hint={(TRANSPORTS.find((t) => t[0] === transport) || [])[2]}>
          <Select T={T} value={transport} data-testid="sapvim-transport"
            onChange={(e) => set('transport', e.target.value)}>
            {TRANSPORTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </Select>
        </Field>
        {transport !== 'sftp' && transport !== 'btp_destination' && (
          <Field T={T} label="authentication">
            <Select T={T} value={authMode} data-testid="sapvim-auth"
              onChange={(e) => set('auth_mode', e.target.value)}>
              {AUTH.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </Select>
          </Field>
        )}

        {transport === 'sftp' && SFTP_FIELDS.map(([k, label]) => (
          <Field T={T} key={k} label={`sftp ${label.toLowerCase()}`}>
            <Input T={T} data-testid={`sapvim-sftp-${k}`}
              value={(draft.sftp || {})[k] ?? ''}
              onChange={(e) => setSftp(k, e.target.value)} />
          </Field>
        ))}
        {transport === 'sftp' && ['sftp_password', 'private_key'].map((k) => (
          <Field T={T} key={k} label={FIELDS[k][0]} hint={FIELDS[k][1]}>
            <Input T={T} data-testid={`sapvim-${k}`} value={draft[k] ?? ''}
              placeholder="leave untouched to keep the stored value"
              onChange={(e) => set(k, e.target.value)} />
          </Field>
        ))}

        {keys.filter((k) => FIELDS[k]).map((k) => {
          const [label, hint, secret] = FIELDS[k];
          return (
            <Field T={T} key={k} label={label} hint={hint}>
              <Input T={T} data-testid={`sapvim-${k}`} value={draft[k] ?? ''}
                placeholder={secret ? 'leave untouched to keep the stored value' : ''}
                onChange={(e) => set(k, e.target.value)} />
            </Field>
          );
        })}

        <Field T={T} label="verify TLS"
          hint="Off is a deliberate, recorded choice — prefer a CA bundle">
          <Select T={T} value={String(draft.verify_tls ?? true)} data-testid="sapvim-verify-tls"
            onChange={(e) => set('verify_tls', e.target.value === 'true')}>
            <option value="true">verify (recommended)</option>
            <option value="false">do not verify</option>
          </Select>
        </Field>
        <Field T={T} label="write-back to SAP"
          hint="Read-only by default. A finance system is not somewhere to write casually.">
          <Select T={T} value={String(draft.write_back_enabled ?? false)}
            data-testid="sapvim-writeback"
            onChange={(e) => set('write_back_enabled', e.target.value === 'true')}>
            <option value="false">off — read only</option>
            <option value="true">on — certification and release recommendation</option>
          </Select>
        </Field>
      </div>
    </Panel>
  );
};

export default SapVimConnect;
